// Lets a screen claim file drops on its own area (shell-ia §12.3): while the claimed element is under the pointer the global drop zone shows
// the screen's label and the files go to its handler instead of the Knowledge Bank. WP-SHELL.
import { useEffect, useRef, type RefObject } from "react";

export interface DropClaim {
  /** "Drop to attach to this file", "Drop to add a version". */
  label: string;
  /** MIME types or extensions this area takes (informational; the handler decides). */
  accepts?: string[];
  onDrop: (files: File[]) => void;
}

const claims = new Map<Element, DropClaim>();

/** The innermost claimed element containing `el`, if any. */
export function claimFor(el: Element): DropClaim | null {
  let node: Element | null = el;
  while (node) {
    const c = claims.get(node);
    if (c) return c;
    node = node.parentElement;
  }
  return null;
}

export function useDropTarget<T extends Element>(claim: DropClaim | null): RefObject<T | null> {
  const ref = useRef<T | null>(null);
  const latest = useRef(claim);
  latest.current = claim;
  useEffect(() => {
    const el = ref.current;
    if (!el || !latest.current) return;
    const proxy: DropClaim = {
      get label() { return latest.current?.label ?? ""; },
      get accepts() { return latest.current?.accepts; },
      onDrop: (files) => latest.current?.onDrop(files),
    };
    claims.set(el, proxy);
    return () => {
      claims.delete(el);
    };
  }, [claim === null]); // eslint-disable-line react-hooks/exhaustive-deps
  return ref;
}
